import { faPlus, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import ReactSelect from 'components/base/ReactSelect';
import SearchBox from 'components/common/SearchBox';
import PatientTable, {
  patientTableColumns
} from 'components/tables/PatientTable';
import ExportFoodDataModal from 'components/modals/ExportFoodDataModal';
import { errorHandler } from 'helpers/response-utils';
import { parseData } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const PatientList = () => {
  const nav = useNavigate();
  const [patientData, setPatientData] = useState([]);
  const [dietitianOptions, setDietitianOptions] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedDietitian, setSelectedDietitian] = useState<any>({
    value: 'all',
    label: 'All'
  });
  const userJSON = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  const currentUser = userJSON != null ? parseData(userJSON) : null;

  // Extract clinic ID, handling both object and primitive types
  const userClinicId = currentUser?.clinic
    ? typeof currentUser.clinic === 'object'
      ? currentUser.clinic.id
      : currentUser.clinic
    : undefined;

  useEffect(() => {
    getList();
    getDietitians();
  }, []);

  const getList = async () => {
    const user = userJSON != null ? parseData(userJSON) : '';

    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/clinic/patient`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response.data['status'] == false) {
      errorHandler(response.data);
      return false;
    }

    setPatientData(response.data['data']['patients']);
  };

  const getDietitians = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/clinic/dietitians`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data['status'] === true) {
        const users = response.data['data']['users'] || [];
        const options = users.map((user: any) => ({
          value: user.id,
          label: user.firstname
        }));
        setDietitianOptions(options);

        const loggedInUserOption = options.find(
          (option: any) => String(option.value) === String(currentUser?.id)
        );
        if (loggedInUserOption) {
          setSelectedDietitian(loggedInUserOption);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dietitians:', error);
    }
  };

  // Filter patients based on selected dietitian
  const filteredPatientData = useMemo(() => {
    if (!selectedDietitian || selectedDietitian.value === 'all') {
      return patientData;
    }
    return (patientData as any[]).filter(
      (patient: any) => patient.dietitian?.id === selectedDietitian.value
    );
  }, [patientData, selectedDietitian]);

  const table = useAdvanceTable({
    data: filteredPatientData,
    columns: patientTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false,
    initialState: {
      columnVisibility: {}
    }
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };
  const navigateToCreateLead = () => {
    nav('/clinic/patient/add');
  };
  return (
    <div>
      <div className="mb-6">
        <AdvanceTableProvider {...table}>
          <h2 className="mb-4">{filteredPatientData.length} Patients</h2>
          <Row className="g-3 align-items-end justify-content-between mb-4">
            <Col xs="auto">
              <div className="d-flex align-items-end gap-2">
                <div>
                  <Form.Label className="d-block mb-2">Search</Form.Label>
                  <SearchBox
                    placeholder="Search by name, file no, email and phone number"
                    onChange={handleSearchInputChange}
                  />
                </div>
                <div style={{ width: '20rem' }}>
                  <Form.Label className="d-block mb-2">Dietitian</Form.Label>
                  <ReactSelect
                    options={[
                      { value: 'all', label: 'All' },
                      ...dietitianOptions
                    ]}
                    placeholder="Select Dietitian"
                    value={selectedDietitian}
                    onChange={(option: any) => setSelectedDietitian(option)}
                    closeMenuOnSelect={true}
                    classNames={{
                      control: () => 'search-input search',
                      option: () => 'px-3'
                    }}
                    styles={{
                      control: (base: any) => ({
                        ...base,
                        minHeight: '38px',
                        height: '38px',
                        paddingLeft: '10px !important',
                        paddingRight: '10px !important'
                      }),
                      valueContainer: (base: any) => ({
                        ...base,
                        paddingLeft: '8px',
                        paddingRight: '8px'
                      })
                    }}
                  />
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-2">
                {currentUser?.role !== 'DIETITIAN' &&
                  (currentUser?.clinical_export_enabled ||
                    currentUser?.research_export_enabled) && (
                    <Button
                      variant="secondary"
                      startIcon={
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                      }
                      onClick={() => setShowExportModal(true)}
                    >
                      Export Data
                    </Button>
                  )}
                <Button
                  variant="primary"
                  className="me-0"
                  startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                  onClick={navigateToCreateLead}
                >
                  Create Patient
                </Button>
              </div>
            </Col>
          </Row>
          <PatientTable />
        </AdvanceTableProvider>
      </div>

      <ExportFoodDataModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        userRole={currentUser?.role || 'clinic'}
        userClinicalExportEnabled={
          currentUser?.clinical_export_enabled || false
        }
        userResearchExportEnabled={
          currentUser?.research_export_enabled || false
        }
        userClinicId={userClinicId}
      />
    </div>
  );
};

export default PatientList;
